/**
 * Audio playback state — shared between the search results UI (which fires
 * "play this citation" events) and the persistent player at the bottom of the
 * page (which renders the actual <audio> element).
 *
 * Plain pub-sub. No need for a state library at this size.
 */

import { audioUrl } from '../api';

export interface PlaybackTarget {
  episode_id: string;
  start_sec: number;
  end_sec: number;
  label: string;          // e.g. "Karpathy on RL — 14:32" — shown in the player chrome
}

export interface PlaybackState {
  current: PlaybackTarget | null;
  queue: PlaybackTarget[];
  position_sec: number;
  is_playing: boolean;
  duration_sec: number | null;
}

type Listener = (state: PlaybackState) => void;

class PlayerStore {
  private state: PlaybackState = {
    current: null,
    queue: [],
    position_sec: 0,
    is_playing: false,
    duration_sec: null,
  };
  private listeners = new Set<Listener>();

  get(): PlaybackState { return this.state; }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  set(next: Partial<PlaybackState>): void {
    this.state = { ...this.state, ...next };
    this.listeners.forEach(fn => fn(this.state));
  }

  /** Pop the next queued item into `current`. Called when a span ends. */
  advance(): void {
    const [next, ...rest] = this.state.queue;
    if (next) {
      this.set({
        current: next,
        queue: rest,
        position_sec: next.start_sec,
        is_playing: true,
      });
    } else {
      this.set({ is_playing: false });
    }
  }

  /** Remove a queued item by index. */
  removeFromQueue(index: number): void {
    this.set({ queue: this.state.queue.filter((_, i) => i !== index) });
  }

  clearQueue(): void {
    this.set({ queue: [] });
  }
}

export const player = new PlayerStore();

/**
 * Build the URL the <audio> element should src from, including the time hash
 * fragment (#t=start,end) which most browsers honour for initial seek.
 */
export function buildPlaybackSrc(target: PlaybackTarget): string {
  const base = audioUrl(target.episode_id);
  return `${base}#t=${target.start_sec.toFixed(2)},${target.end_sec.toFixed(2)}`;
}

// ────────────────────────────────────────────────────────────────────────
// Citation-click behavior — the "what happens when the user clicks [3]?"
// decision lives here.
//
// Three sensible options:
//   A. REPLACE: load that episode at start_sec and autoplay. End at episode end.
//      Conventional, like Spotify. Simple to reason about.
//   B. PREVIEW: play just from start_sec to end_sec, then pause. Optimised for
//      audit ("did the speaker really say that?"). Loses momentum if the user
//      wants to keep listening past the citation.
//   C. QUEUE: append to a play-queue, stream through citations in order.
//      Best for "browse mode". More UI surface area to build.
//
// Default: A. The TODO below is the user-input seam.
// ────────────────────────────────────────────────────────────────────────

export type CitationClickMode = 'replace' | 'preview' | 'queue';

export const CITATION_CLICK_MODE: CitationClickMode = 'queue';

export function onCitationClick(target: PlaybackTarget): void {
  const state = player.get();
  switch (CITATION_CLICK_MODE) {
    case 'replace':
      player.set({ current: target, position_sec: target.start_sec, is_playing: true });
      break;
    case 'preview':
      // The persistent player observes this and stops at end_sec on its own.
      player.set({ current: target, position_sec: target.start_sec, is_playing: true });
      break;
    case 'queue':
      // First click: start now. Subsequent clicks: append to queue.
      // The player auto-advances at end_sec (see AudioPlayer.tsx + player.advance()).
      if (!state.current) {
        player.set({ current: target, position_sec: target.start_sec, is_playing: true });
      } else {
        player.set({ queue: [...state.queue, target] });
      }
      break;
  }
}
